import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import CFiltersBar from '../shared/c-filters-bar';
import usePersistedState from '../../hooks/usePersistedState';

// One status list for both queues: the states they share, plus Rejected, which
// only from-home rows have. A queue asks for everything when the picked value
// means nothing to it.
export const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'decided', label: 'Acknowledged or approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

export const amendmentStatus = (status) => {
  if (status === 'pending') return 'pending';
  if (status === 'decided') return 'acknowledged';
  return 'all';
};

export const wfhStatus = (status) => {
  if (status === 'decided') return 'approved';
  if (status === 'pending' || status === 'rejected') return status;
  return 'all';
};

/**
 * The filter values both approval queues read. They live above the sections so
 * one row drives both, and the department list is fetched once for both.
 */
export const useApprovalFilters = ({ canReadAll, enabled }) => {
  const [status, setStatus] = usePersistedState('approvals.status', 'pending');
  const [departmentId, setDepartmentId] = usePersistedState('approvals.department', '');
  const [search, setSearch] = usePersistedState('approvals.search', '');
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!enabled || !canReadAll) return;
    api.get('/api/employees/departments/list')
      .then((res) => setDepartments(res.data.data || []))
      .catch(() => {});
  }, [enabled, canReadAll]);

  const clear = () => {
    setStatus('pending');
    setDepartmentId('');
    setSearch('');
  };

  return {
    status,
    setStatus,
    departmentId,
    setDepartmentId,
    search,
    setSearch,
    debouncedSearch,
    departments,
    canReadAll,
    clear,
  };
};

const ApprovalFilters = ({ filters }) => (
  <CFiltersBar
    searchTerm={filters.search}
    onSearchChange={(event) => filters.setSearch(event.target.value)}
    searchPlaceholder="Search employee..."
    searchTestId="approvals-search"
    clearTestId="approvals-clear-filters"
    filters={[
      {
        key: 'approvals-status',
        testId: 'approvals-status-filter',
        label: 'Status',
        value: filters.status,
        onChange: (event) => filters.setStatus(event.target.value),
        options: STATUS_OPTIONS,
      },
      ...(filters.canReadAll ? [{
        key: 'approvals-department',
        testId: 'approvals-department-filter',
        label: 'Department',
        value: filters.departmentId,
        onChange: (event) => filters.setDepartmentId(event.target.value),
        options: [
          { value: '', label: 'All Departments' },
          ...filters.departments.map((department) => ({ value: String(department.id), label: department.name })),
        ],
      }] : []),
    ]}
    onClear={filters.clear}
  />
);

export default ApprovalFilters;
